<template>
  <div class="pt-4 px-4">
    <div class="mb-3 d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center">
        <h2>AI Debug Logs</h2>
        <span v-if="totalCount > 0" class="ms-3 text-muted">({{ totalCount }} total entries)</span>
      </div>

      <div class="d-flex align-items-center">
        <label class="me-2 form-label">Show:</label>
        <select class="form-select" v-model="limit" @change="applyFilters" style="width: auto;">
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </div>
    </div>

    <div class="mb-3 d-flex flex-wrap gap-2">
      <div class="d-flex align-items-center">
        <label class="me-2 form-label">Feature Type:</label>
        <input 
          type="text" 
          class="form-control" 
          v-model="featureTypeFilter" 
          @input="applyFilters"
          placeholder="Filter by feature type..."
          style="width: 180px;"
        />
      </div>

      <div class="d-flex align-items-center">
        <label class="me-2 form-label">Has Errors:</label>
        <select class="form-select" v-model="hasErrorsFilter" @change="applyFilters" style="width: auto;">
          <option value="">All</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </div>

      <div class="d-flex align-items-center">
        <label class="me-2 form-label">Route:</label>
        <input 
          type="text" 
          class="form-control" 
          v-model="routeFilter" 
          @input="applyFilters"
          placeholder="Filter by route..."
          style="width: 200px;"
        />
      </div>

      <div class="d-flex align-items-center">
        <label class="me-2 form-label">User ID:</label>
        <input 
          type="text" 
          class="form-control" 
          v-model="userIdFilter" 
          @input="applyFilters"
          placeholder="Filter by user ID..."
          style="width: 150px;"
        />
      </div>
    </div>

    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="debugLogs.length === 0" class="py-3">
      <p class="text-muted">No debug logs found.</p>
    </div>

    <div v-else class="table-responsive table-container scrollable-container">
      <table class="table table-hover">
        <thead class="bg-gray-500 text-uppercase">
          <tr>
            <th style="width: 80px;">ID</th>
            <th style="width: 180px;">Timestamp</th>
            <th style="width: 150px;">Route</th>
            <th style="width: 120px;">Feature Type</th>
            <th style="width: 100px;">User ID</th>
            <th style="width: 120px;">Initiation</th>
            <th style="width: 100px;">Status</th>
            <th class="text-end">Click row for details</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="log in debugLogs" 
            :key="log.debugId"
            @click="viewDetails(log)"
            class="clickable-row"
            :class="{ 
              'table-danger': log.hasErrors,
              'table-info': !log.hasErrors
            }"
          >
            <td>
              <strong>{{ log.debugId }}</strong>
            </td>
            <td>
              <div class="text-truncate" style="max-width: 160px;">
                {{ formatTimestamp(log.debugTimestamp) }}
              </div>
            </td>
            <td>
              <span v-if="log.route" class="badge bg-secondary text-truncate" style="max-width: 130px;">
                {{ log.route }}
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span v-if="log.featureType" class="badge bg-info">{{ log.featureType }}</span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              {{ log.userId || '-' }}
            </td>
            <td>
              <span v-if="log.initiationType" class="badge bg-warning">{{ log.initiationType }}</span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span 
                class="badge" 
                :class="log.hasErrors ? 'bg-danger' : 'bg-success'"
              >
                {{ log.hasErrors ? 'Error' : 'Success' }}
              </span>
            </td>
            <td class="text-end">
              <i class="fas fa-chevron-right text-muted"></i>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="debugLogs.length > 0" class="d-flex justify-content-between align-items-center mt-3">
      <div>
        <button
          class="btn btn-primary me-2"
          @click="loadPreviousPage"
          :disabled="loading || offset === 0"
        >
          Previous Page
        </button>
        <button 
          class="btn btn-primary" 
          @click="loadNextPage" 
          :disabled="loading || debugLogs.length < limit"
        >
          Next Page
        </button>
      </div>
      
      <div class="text-muted">
        Showing {{ offset + 1 }} - {{ offset + debugLogs.length }} of {{ totalCount }} entries
      </div>
    </div>

    <div v-if="showDetailsModal" class="modal fade show" style="display: block;" tabindex="-1">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <div>
              <h5 class="modal-title">Debug Log Details</h5>
              <small class="text-muted">ID: {{ selectedLog.debugId }} • {{ formatTimestamp(selectedLog.debugTimestamp) }}</small>
            </div>
            <button type="button" class="btn-close" @click="closeDetailsModal"></button>
          </div>
          <div class="modal-body">
            
            <!-- Loading State -->
            <div v-if="loadingDetails" class="text-center py-5">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading details...</span>
              </div>
              <p class="mt-2 text-muted">Loading full log details...</p>
            </div>

            <!-- Content when loaded -->
            <div v-else-if="selectedLogDetails">
              
              <div class="alert" :class="{
                'alert-danger': selectedLogDetails.hasErrors,
                'alert-warning': selectedLogDetails.severity === 'warning' && !selectedLogDetails.hasErrors,
                'alert-info': !selectedLogDetails.hasErrors && selectedLogDetails.severity !== 'warning'
              }">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Status: </strong>
                    <span class="badge" :class="{
                      'bg-danger': selectedLogDetails.hasErrors,
                      'bg-warning': selectedLogDetails.severity === 'warning' && !selectedLogDetails.hasErrors,
                      'bg-success': !selectedLogDetails.hasErrors && selectedLogDetails.severity !== 'warning'
                    }">
                      {{ selectedLogDetails.hasErrors ? 'Error' : (selectedLogDetails.severity || 'info').toUpperCase() }}
                    </span>
                  </div>
                  <div v-if="selectedLogDetails.responseTimeMs || selectedLogDetails.totalTokens">
                    <span v-if="selectedLogDetails.responseTimeMs" class="me-3">
                      <i class="fas fa-clock"></i> {{ selectedLogDetails.responseTimeMs }}ms
                    </span>
                    <span v-if="selectedLogDetails.totalTokens">
                      <i class="fas fa-coins"></i> {{ selectedLogDetails.totalTokens }} tokens
                    </span>
                  </div>
                </div>
              </div>

              <ul class="nav nav-tabs" id="debugTabs" role="tablist">
                <li class="nav-item" role="presentation">
                  <button class="nav-link active" id="overview-tab" data-bs-toggle="tab" data-bs-target="#overview" type="button" role="tab">
                    Overview
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button class="nav-link" id="conversation-tab" data-bs-toggle="tab" data-bs-target="#conversation" type="button" role="tab">
                    Conversation
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button class="nav-link" id="performance-tab" data-bs-toggle="tab" data-bs-target="#performance" type="button" role="tab">
                    Performance
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button class="nav-link" id="technical-tab" data-bs-toggle="tab" data-bs-target="#technical" type="button" role="tab">
                    Technical
                  </button>
                </li>
                <li v-if="selectedLogDetails.hasErrors || (selectedLogDetails.errorMessages && selectedLogDetails.errorMessages.length > 0)" class="nav-item" role="presentation">
                  <button class="nav-link text-danger" id="errors-tab" data-bs-toggle="tab" data-bs-target="#errors" type="button" role="tab">
                    Errors
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button class="nav-link text-info" id="raw-data-tab" data-bs-toggle="tab" data-bs-target="#raw-data" type="button" role="tab">
                    Raw Data
                  </button>
                </li>
              </ul>

              <div class="tab-content mt-3" id="debugTabContent">
                
                <div class="tab-pane fade show active" id="overview" role="tabpanel">
                  <div class="row">
                    <div class="col-md-6">
                      <h6>Basic Information</h6>
                      <table class="table table-sm table-borderless">
                        <tr><td class="text-muted" style="width: 40%;">User ID:</td><td>{{ selectedLogDetails.userId || 'N/A' }}</td></tr>
                        <tr><td class="text-muted">Customer ID:</td><td>{{ selectedLogDetails.customerId || 'N/A' }}</td></tr>
                        <tr><td class="text-muted">Route:</td><td><code class="small">{{ selectedLogDetails.route || 'N/A' }}</code></td></tr>
                        <tr><td class="text-muted">Feature Type:</td><td>{{ selectedLogDetails.featureType || 'N/A' }}</td></tr>
                        <tr><td class="text-muted">Workflow Type:</td><td>{{ selectedLogDetails.workflowType || 'N/A' }}</td></tr>
                        <tr><td class="text-muted">LLM Model:</td><td>{{ selectedLogDetails.llmModel || 'N/A' }}</td></tr>
                        <tr><td class="text-muted">Initiation Type:</td><td>{{ selectedLogDetails.initiationType || 'N/A' }}</td></tr>
                        <tr><td class="text-muted">Severity:</td><td>{{ selectedLogDetails.severity || 'N/A' }}</td></tr>
                      </table>
                    </div>
                    <div class="col-md-6">
                      <h6>Context Information</h6>
                      <table class="table table-sm table-borderless">
                        <tr><td class="text-muted" style="width: 40%;">Conversation ID:</td><td>{{ selectedLogDetails.conversationId || 'N/A' }}</td></tr>
                        <tr><td class="text-muted">Interaction ID:</td><td>{{ selectedLogDetails.interactionId || 'N/A' }}</td></tr>
                        <tr><td class="text-muted">Conversation Name:</td><td>{{ selectedLogDetails.conversationName || 'N/A' }}</td></tr>
                        <tr><td class="text-muted">New Conversation:</td><td>{{ selectedLogDetails.isNewConversation ? 'Yes' : 'No' }}</td></tr>
                        <tr v-if="selectedLogDetails.conversationPreview"><td class="text-muted">Preview:</td><td class="small">{{ selectedLogDetails.conversationPreview }}</td></tr>
                        <tr><td class="text-muted">Created At:</td><td>{{ formatTimestamp(selectedLogDetails.createdAt) }}</td></tr>
                        <tr><td class="text-muted">Updated At:</td><td>{{ formatTimestamp(selectedLogDetails.updatedAt) }}</td></tr>
                      </table>
                    </div>
                  </div>
                </div>

                <div class="tab-pane fade" id="conversation" role="tabpanel">
                  <div v-if="selectedLogDetails.userPrompt" class="mb-4">
                    <h6>User Prompt</h6>
                    <div class="bg-light border rounded p-3">
                      <pre class="mb-0 small">{{ selectedLogDetails.userPrompt }}</pre>
                    </div>
                  </div>

                  <div v-if="selectedLogDetails.userMessages" class="mb-4">
                    <h6>User Messages</h6>
                    <div class="bg-light border rounded p-3">
                      <pre class="mb-0 small">{{ selectedLogDetails.userMessages }}</pre>
                    </div>
                  </div>

                  <div v-if="selectedLogDetails.llmResponse" class="mb-4">
                    <h6>LLM Response</h6>
                    <div class="bg-light border rounded p-3" style="max-height: 400px; overflow-y: auto;">
                      <json-viewer :data="selectedLogDetails.llmResponse" />
                    </div>
                  </div>

                  <div v-if="selectedLogDetails.messages && selectedLogDetails.messages.length > 0" class="mb-4">
                    <h6>All Messages</h6>
                    <div class="bg-light border rounded p-3" style="max-height: 400px; overflow-y: auto;">
                      <json-viewer :data="selectedLogDetails.messages" />
                    </div>
                  </div>
                </div>

                <div class="tab-pane fade" id="performance" role="tabpanel">
                  <div class="row">
                    <div class="col-md-6">
                      <h6>Timing & Tokens</h6>
                      <table class="table table-sm table-borderless">
                        <tr><td class="text-muted" style="width: 50%;">Response Time:</td><td>{{ selectedLogDetails.responseTimeMs || 'N/A' }} ms</td></tr>
                        <tr><td class="text-muted">Prompt Tokens:</td><td>{{ selectedLogDetails.promptTokens || 'N/A' }}</td></tr>
                        <tr><td class="text-muted">Completion Tokens:</td><td>{{ selectedLogDetails.completionTokens || 'N/A' }}</td></tr>
                        <tr><td class="text-muted">Total Tokens:</td><td>{{ selectedLogDetails.totalTokens || 'N/A' }}</td></tr>
                      </table>
                    </div>
                    <div class="col-md-6">
                      <h6>Tool Execution</h6>
                      <table class="table table-sm table-borderless">
                        <tr><td class="text-muted" style="width: 50%;">Tool Execution Count:</td><td>{{ selectedLogDetails.toolExecutionCount || 'N/A' }}</td></tr>
                        <tr><td class="text-muted">Max Steps Allowed:</td><td>{{ selectedLogDetails.maxStepsAllowed || 'N/A' }}</td></tr>
                      </table>
                    </div>
                  </div>

                  <div v-if="selectedLogDetails.toolsCalled && selectedLogDetails.toolsCalled.length > 0" class="mt-4">
                    <h6>Tools Called</h6>
                    <div class="bg-light border rounded p-3" style="max-height: 300px; overflow-y: auto;">
                      <json-viewer :data="selectedLogDetails.toolsCalled" />
                    </div>
                  </div>

                  <div v-if="selectedLogDetails.toolResults && selectedLogDetails.toolResults.length > 0" class="mt-4">
                    <h6>Tool Results</h6>
                    <div class="bg-light border rounded p-3" style="max-height: 300px; overflow-y: auto;">
                      <json-viewer :data="selectedLogDetails.toolResults" />
                    </div>
                  </div>

                  <div v-if="selectedLogDetails.promptIds && selectedLogDetails.promptIds.length > 0" class="mt-4">
                    <h6>Prompt IDs</h6>
                    <div class="bg-light border rounded p-3">
                      <span v-for="promptId in selectedLogDetails.promptIds" :key="promptId" class="badge bg-info me-1">{{ promptId }}</span>
                    </div>
                  </div>

                  <div v-if="selectedLogDetails.systemPromptDetails && Object.keys(selectedLogDetails.systemPromptDetails).length > 0" class="mt-4">
                    <h6>System Prompt Details</h6>
                    <div class="bg-light border rounded p-3" style="max-height: 300px; overflow-y: auto;">
                      <json-viewer :data="selectedLogDetails.systemPromptDetails" />
                    </div>
                  </div>
                </div>

                <div class="tab-pane fade" id="technical" role="tabpanel">
                  <div class="row">
                    <div class="col-md-6">
                      <h6>System Context</h6>
                      <div v-if="selectedLogDetails.dto && Object.keys(selectedLogDetails.dto).length > 0" class="mb-3">
                        <strong class="small">DTO:</strong>
                        <div class="bg-light border rounded p-2 mt-1" style="max-height: 200px; overflow-y: auto;">
                          <json-viewer :data="selectedLogDetails.dto" />
                        </div>
                      </div>
                      <div v-if="selectedLogDetails.requestContext && Object.keys(selectedLogDetails.requestContext).length > 0" class="mb-3">
                        <strong class="small">Request Context:</strong>
                        <div class="bg-light border rounded p-2 mt-1" style="max-height: 200px; overflow-y: auto;">
                          <json-viewer :data="selectedLogDetails.requestContext" />
                        </div>
                      </div>
                      <div v-if="selectedLogDetails.conversationContext && Object.keys(selectedLogDetails.conversationContext).length > 0" class="mb-3">
                        <strong class="small">Conversation Context:</strong>
                        <div class="bg-light border rounded p-2 mt-1" style="max-height: 200px; overflow-y: auto;">
                          <json-viewer :data="selectedLogDetails.conversationContext" />
                        </div>
                      </div>
                      <div v-if="selectedLogDetails.conversationMetadata && Object.keys(selectedLogDetails.conversationMetadata).length > 0" class="mb-3">
                        <strong class="small">Conversation Metadata:</strong>
                        <div class="bg-light border rounded p-2 mt-1" style="max-height: 200px; overflow-y: auto;">
                          <json-viewer :data="selectedLogDetails.conversationMetadata" />
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <h6>Feature Context</h6>
                      <div v-if="selectedLogDetails.featureContext && Object.keys(selectedLogDetails.featureContext).length > 0" class="mb-3">
                        <strong class="small">Feature Context:</strong>
                        <div class="bg-light border rounded p-2 mt-1" style="max-height: 200px; overflow-y: auto;">
                          <json-viewer :data="selectedLogDetails.featureContext" />
                        </div>
                      </div>
                      <div v-if="selectedLogDetails.userPreferences && Object.keys(selectedLogDetails.userPreferences).length > 0" class="mb-3">
                        <strong class="small">User Preferences:</strong>
                        <div class="bg-light border rounded p-2 mt-1" style="max-height: 200px; overflow-y: auto;">
                          <json-viewer :data="selectedLogDetails.userPreferences" />
                        </div>
                      </div>
                      <div v-if="selectedLogDetails.savedFilters && Object.keys(selectedLogDetails.savedFilters).length > 0" class="mb-3">
                        <strong class="small">Saved Filters:</strong>
                        <div class="bg-light border rounded p-2 mt-1" style="max-height: 200px; overflow-y: auto;">
                          <json-viewer :data="selectedLogDetails.savedFilters" />
                        </div>
                      </div>
                      <div v-if="selectedLogDetails.metaData && Object.keys(selectedLogDetails.metaData).length > 0" class="mb-3">
                        <strong class="small">Metadata:</strong>
                        <div class="bg-light border rounded p-2 mt-1" style="max-height: 200px; overflow-y: auto;">
                          <json-viewer :data="selectedLogDetails.metaData" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Parameter Fields Section -->
                  <div v-if="hasParameterFields" class="mt-4">
                    <h6>Parameter Fields</h6>
                    <div class="row">
                      <div class="col-md-6">
                        <div v-if="selectedLogDetails.filterInventoryParams && Object.keys(selectedLogDetails.filterInventoryParams).length > 0" class="mb-3">
                          <strong class="small">Filter Inventory Params:</strong>
                          <div class="bg-light border rounded p-2 mt-1" style="max-height: 150px; overflow-y: auto;">
                            <json-viewer :data="selectedLogDetails.filterInventoryParams" />
                          </div>
                        </div>
                        <div v-if="selectedLogDetails.getTopKParams && Object.keys(selectedLogDetails.getTopKParams).length > 0" class="mb-3">
                          <strong class="small">Get Top K Params:</strong>
                          <div class="bg-light border rounded p-2 mt-1" style="max-height: 150px; overflow-y: auto;">
                            <json-viewer :data="selectedLogDetails.getTopKParams" />
                          </div>
                        </div>
                        <div v-if="selectedLogDetails.summarizeInventoryParams && Object.keys(selectedLogDetails.summarizeInventoryParams).length > 0" class="mb-3">
                          <strong class="small">Summarize Inventory Params:</strong>
                          <div class="bg-light border rounded p-2 mt-1" style="max-height: 150px; overflow-y: auto;">
                            <json-viewer :data="selectedLogDetails.summarizeInventoryParams" />
                          </div>
                        </div>
                        <div v-if="selectedLogDetails.getScenarioSelectedInventoryParams && Object.keys(selectedLogDetails.getScenarioSelectedInventoryParams).length > 0" class="mb-3">
                          <strong class="small">Get Scenario Selected Inventory Params:</strong>
                          <div class="bg-light border rounded p-2 mt-1" style="max-height: 150px; overflow-y: auto;">
                            <json-viewer :data="selectedLogDetails.getScenarioSelectedInventoryParams" />
                          </div>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div v-if="selectedLogDetails.filterProcedureParams && Object.keys(selectedLogDetails.filterProcedureParams).length > 0" class="mb-3">
                          <strong class="small">Filter Procedure Params:</strong>
                          <div class="bg-light border rounded p-2 mt-1" style="max-height: 150px; overflow-y: auto;">
                            <json-viewer :data="selectedLogDetails.filterProcedureParams" />
                          </div>
                        </div>
                        <div v-if="selectedLogDetails.getTopKProcedureParams && Object.keys(selectedLogDetails.getTopKProcedureParams).length > 0" class="mb-3">
                          <strong class="small">Get Top K Procedure Params:</strong>
                          <div class="bg-light border rounded p-2 mt-1" style="max-height: 150px; overflow-y: auto;">
                            <json-viewer :data="selectedLogDetails.getTopKProcedureParams" />
                          </div>
                        </div>
                        <div v-if="selectedLogDetails.summarizeInventoryProcedureParams && Object.keys(selectedLogDetails.summarizeInventoryProcedureParams).length > 0" class="mb-3">
                          <strong class="small">Summarize Inventory Procedure Params:</strong>
                          <div class="bg-light border rounded p-2 mt-1" style="max-height: 150px; overflow-y: auto;">
                            <json-viewer :data="selectedLogDetails.summarizeInventoryProcedureParams" />
                          </div>
                        </div>
                        <div v-if="selectedLogDetails.getScenarioSelectedInventoryProcedureParams && Object.keys(selectedLogDetails.getScenarioSelectedInventoryProcedureParams).length > 0" class="mb-3">
                          <strong class="small">Get Scenario Selected Inventory Procedure Params:</strong>
                          <div class="bg-light border rounded p-2 mt-1" style="max-height: 150px; overflow-y: auto;">
                            <json-viewer :data="selectedLogDetails.getScenarioSelectedInventoryProcedureParams" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Redis Keys -->
                  <div v-if="selectedLogDetails.redisKeys && selectedLogDetails.redisKeys.length > 0" class="mt-4">
                    <h6>Redis Keys</h6>
                    <div class="bg-light border rounded p-3">
                      <div v-for="key in selectedLogDetails.redisKeys" :key="key" class="mb-1">
                        <code class="small">{{ key }}</code>
                      </div>
                    </div>
                  </div>

                  <div v-if="selectedLogDetails.debugNotes" class="mt-4">
                    <h6>Debug Notes</h6>
                    <div class="alert alert-info">{{ selectedLogDetails.debugNotes }}</div>
                  </div>

                  <div v-if="selectedLogDetails.tags && selectedLogDetails.tags.length > 0" class="mt-3">
                    <h6>Tags</h6>
                    <div>
                      <span v-for="tag in selectedLogDetails.tags" :key="tag" class="badge bg-secondary me-1">{{ tag }}</span>
                    </div>
                  </div>
                </div>

                <div v-if="selectedLogDetails.hasErrors || (selectedLogDetails.errorMessages && selectedLogDetails.errorMessages.length > 0)" class="tab-pane fade" id="errors" role="tabpanel">
                  <div v-if="selectedLogDetails.errorMessages && selectedLogDetails.errorMessages.length > 0" class="mb-4">
                    <h6>Error Messages</h6>
                    <div class="alert alert-danger">
                      <json-viewer :data="selectedLogDetails.errorMessages" />
                    </div>
                  </div>

                  <div v-if="selectedLogDetails.exceptions && selectedLogDetails.exceptions.length > 0" class="mb-4">
                    <h6>Exceptions</h6>
                    <div class="alert alert-danger">
                      <json-viewer :data="selectedLogDetails.exceptions" />
                    </div>
                  </div>

                  <div v-if="selectedLogDetails.audienceFiltersValidationMessage" class="mb-4">
                    <h6>Validation Message</h6>
                    <div class="alert alert-warning">{{ selectedLogDetails.audienceFiltersValidationMessage }}</div>
                  </div>
                </div>

                <!-- Raw Data Tab for Debugging -->
                <div class="tab-pane fade" id="raw-data" role="tabpanel">
                  <div class="alert alert-info">
                    <i class="fas fa-info-circle me-2"></i>
                    This tab shows all raw data received from the API. Use this for debugging missing fields.
                  </div>
                  
                  <h6>Complete Raw Response</h6>
                  <div class="bg-light border rounded p-3" style="max-height: 600px; overflow-y: auto;">
                    <json-viewer :data="selectedLogDetails" />
                  </div>

                  <h6 class="mt-4">Available Fields</h6>
                  <div class="bg-light border rounded p-3">
                    <div class="row">
                      <div class="col-md-6">
                        <strong class="small">Field Names:</strong>
                        <div v-for="field in availableFields" :key="field" class="small">
                          <span class="badge bg-secondary me-1">{{ field }}</span>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <strong class="small">Field Count:</strong> {{ availableFields.length }}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <div v-else class="text-center py-5">
              <div class="alert alert-warning">
                <i class="fas fa-exclamation-triangle me-2"></i>
                Failed to load detailed information. Please try again.
              </div>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDetailsModal">Close</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showDetailsModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import AiDebugLogService from "@/services/AiDebugLogService";

const JsonViewer = {
  props: ['data'],
  template: `<pre class="json-viewer">{{ formatJson(data) }}</pre>`,
  methods: {
    formatJson(data) {
      if (!data) return 'null';
      if (typeof data === 'string') {
        try {
          return JSON.stringify(JSON.parse(data), null, 2);
        } catch {
          return data;
        }
      }
      return JSON.stringify(data, null, 2);
    }
  }
};

export default {
  name: "AiDebugLogs",
  components: {
    JsonViewer
  },
  data() {
    return {
      allDebugLogs: [],
      loading: false,
      loadingDetails: false,
      limit: 50,
      offset: 0,
      featureTypeFilter: "",
      hasErrorsFilter: "",
      routeFilter: "",
      userIdFilter: "",
      showDetailsModal: false,
      selectedLog: null,
      selectedLogDetails: null,
    };
  },
  computed: {
    filteredLogs() {
      let filtered = [...this.allDebugLogs];

      if (this.featureTypeFilter) {
        const featureQuery = this.featureTypeFilter.toLowerCase();
        filtered = filtered.filter(log => 
          log.featureType && log.featureType.toLowerCase().includes(featureQuery)
        );
      }

      if (this.hasErrorsFilter) {
        const hasErrors = this.hasErrorsFilter === 'true';
        filtered = filtered.filter(log => log.hasErrors === hasErrors);
      }

      if (this.routeFilter) {
        const routeQuery = this.routeFilter.toLowerCase();
        filtered = filtered.filter(log => 
          log.route && log.route.toLowerCase().includes(routeQuery)
        );
      }

      if (this.userIdFilter) {
        const userIdQuery = this.userIdFilter.toLowerCase();
        filtered = filtered.filter(log => {
          if (!log.userId) return false;
          const userIdStr = log.userId.toString().toLowerCase();
          return userIdStr.includes(userIdQuery);
        });
      }

      return filtered;
    },
    
    debugLogs() {
      const start = this.offset;
      const end = start + parseInt(this.limit);
      return this.filteredLogs.slice(start, end);
    },

    totalCount() {
      return this.filteredLogs.length;
    },
    hasParameterFields() {
      if (!this.selectedLogDetails) return false;
      const details = this.selectedLogDetails;
      return (
        (details.filterInventoryParams && Object.keys(details.filterInventoryParams).length > 0) ||
        (details.getTopKParams && Object.keys(details.getTopKParams).length > 0) ||
        (details.summarizeInventoryParams && Object.keys(details.summarizeInventoryParams).length > 0) ||
        (details.getScenarioSelectedInventoryParams && Object.keys(details.getScenarioSelectedInventoryParams).length > 0) ||
        (details.filterProcedureParams && Object.keys(details.filterProcedureParams).length > 0) ||
        (details.getTopKProcedureParams && Object.keys(details.getTopKProcedureParams).length > 0) ||
        (details.summarizeInventoryProcedureParams && Object.keys(details.summarizeInventoryProcedureParams).length > 0) ||
        (details.getScenarioSelectedInventoryProcedureParams && Object.keys(details.getScenarioSelectedInventoryProcedureParams).length > 0)
      );
    },
    availableFields() {
      if (!this.selectedLogDetails) return [];
      return Object.keys(this.selectedLogDetails).sort();
    }
  },
  mounted() {
    this.loadPage();
  },
  methods: {
    loadPage() {
      this.loading = true;
      const service = new AiDebugLogService();
      
      let url = `/v3/api/planning/ai-debug-logs`;

      service
        .executeGet(service.baseUrl + url)
        .then((response) => {
          this.allDebugLogs = response.data || response;
          this.loading = false;
        })
        .catch((error) => {
          console.log("Error loading AI debug logs:", error);
          this.allDebugLogs = [];
          this.loading = false;
        });
    },
    loadNextPage() {
      const maxOffset = Math.max(0, this.totalCount - parseInt(this.limit));
      if (this.offset < maxOffset) {
        this.offset += parseInt(this.limit);
      }
    },
    loadPreviousPage() {
      if (this.offset > 0) {
        this.offset = Math.max(0, this.offset - parseInt(this.limit));
      }
    },
    applyFilters() {
      this.offset = 0;
    },
    viewDetails(log) {
      this.selectedLog = log;
      this.selectedLogDetails = null;
      this.showDetailsModal = true;
      this.loadingDetails = true;

      // Fetch full details for this log
      const service = new AiDebugLogService();
      service.getById(log.debugId)
        .then((response) => {
          this.selectedLogDetails = response.data || response;
          this.loadingDetails = false;
          
          // Debug logging to help identify missing fields
          console.log('=== AI Debug Log Details ===');
          console.log('Raw response:', response);
          console.log('Selected log details:', this.selectedLogDetails);
          console.log('Available fields:', Object.keys(this.selectedLogDetails));
          console.log('Fields with data:', Object.keys(this.selectedLogDetails).filter(key => {
            const value = this.selectedLogDetails[key];
            if (value === null || value === undefined) return false;
            if (typeof value === 'string' && value.trim() === '') return false;
            if (Array.isArray(value) && value.length === 0) return false;
            if (typeof value === 'object' && Object.keys(value).length === 0) return false;
            return true;
          }));
          console.log('=== End Debug Info ===');
        })
        .catch((error) => {
          console.log("Error loading debug log details:", error);
          this.loadingDetails = false;
          // Fallback to basic log data if details fetch fails
          this.selectedLogDetails = log;
        });

      this.$nextTick(() => {
        const firstTab = document.querySelector('#overview-tab');
        if (firstTab) {
          firstTab.click();
        }
      });
    },
    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedLog = null;
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return 'N/A';
      return new Date(timestamp).toLocaleString();
    },
  },
};
</script>

<style scoped>
.table-container {
  max-height: 70vh;
}

.scrollable-container {
  overflow-y: auto;
}

.form-label {
  white-space: nowrap;
  margin-bottom: 0;
}

.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.clickable-row:hover {
  background-color: rgba(0, 123, 255, 0.1);
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.badge {
  font-size: 0.75em;
}

.json-viewer {
  font-size: 0.8em;
  max-height: 300px;
  overflow-y: auto;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.nav-tabs .nav-link {
  font-size: 0.9em;
}

.tab-content {
  min-height: 400px;
}

code {
  font-size: 0.85em;
}
</style> 